/* eslint-disable @typescript-eslint/no-explicit-any */
/*
 * File: useKingstonLivingstonService.ts
 * Project: papa-bear
 * -----
 * Last Modified: 23 Nov 2021 3:49:49 pm
 * -----
 * Copyright (c) 2021 Elegant Software Solutions All rights reserved.
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { encodeValue } from 'common/helpers/encodeValue';
import lodash from 'lodash';
import { SiteModel } from 'models/SiteModel';
import { WarehouseModel } from 'models/WarehouseModel';
import { ReduxState } from 'redux/rootReducer';

/* ****************** Interfaces ****************** */
export interface GetSitesParams {
  dataAreaId?: string;
  siteContains?: string;
  offset: number;
  overrideSkipTake?: boolean
}

export interface GetSitesResponseModel {
  dataAreaId?: string;
  data: SiteModel[];
  totalCount: number;
}
export interface GetWarehousesParams {
  dataAreaId?: string;
  warehouseContains?: string;
  site?: string;
  offset: number;
  overrideSkipTake?: boolean
}

export interface GetWarehousesResponseModel {
  dataAreaId?: string;
  data: WarehouseModel[];
  totalCount: number;
}

/* ****************** Variables ****************** */
const kingstonLivingstonBaseUrl = process.env.REACT_APP_KINGSTON_LIVINGSTON_BASE_URL;
const functionsKey = process.env.REACT_APP_API_HOST_KEY_KINGSTON_LIVINGSTON;

const baseQuery = fetchBaseQuery({
  baseUrl: kingstonLivingstonBaseUrl,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as ReduxState).app.accessToken;

    if (token) {
      headers.set('authorization', `Bearer ${token}`);
      headers.set('x-functions-key', functionsKey);
      headers.set('Content-Type', 'application/json');
    }

    return headers;
  }
});

export const warehouseSiteApi = createApi({
  reducerPath: 'warehouseSiteApi',
  baseQuery,
  endpoints: (builder) => ({
    getSites: builder.query<GetSitesResponseModel, GetSitesParams>({
      query: ({ dataAreaId, offset, siteContains, overrideSkipTake }) => ({
        url: `sites`,
        params: {
          dataAreaId: dataAreaId ? encodeValue({ isUrl: false, value: dataAreaId }) : undefined,
          offset,
          siteContains,
          overrideSkipTake
        }
      }),
      onCacheEntryAdded: async (_arg, _api) => {
        try {
          await _api.cacheDataLoaded;
          const { originalArgs } = _api.getCacheEntry();
          const { queries } = _api.getState().warehouseSiteApi;
          const lastQueryArgs = { ...originalArgs, offset: _arg.offset - 25 };
          const lastQueryData = Object.values(queries).find((value) => lodash.isEqual(value?.originalArgs, lastQueryArgs));

          if (lastQueryData) {
            _api.updateCachedData((draft) => {
              draft.data.unshift(...(lastQueryData.data as any).data);
            });
          }
        } catch (error) {
          console.log(error);
        }
      }
    }),
    getWarehouses: builder.query<GetWarehousesResponseModel, GetWarehousesParams>({
      query: ({ dataAreaId, site, offset, warehouseContains, overrideSkipTake }) => ({
        url: `warehouses`,
        params: {
          dataAreaId: dataAreaId ? encodeValue({ isUrl: false, value: dataAreaId }) : undefined,
          site: site ? encodeValue({ isUrl: false, value: site }) : undefined,
          offset,
          warehouseContains,
          overrideSkipTake
        }
      }),
      onCacheEntryAdded: async (_arg, _api) => {
        try {
          await _api.cacheDataLoaded;
          const { originalArgs } = _api.getCacheEntry();
          const { queries } = _api.getState().warehouseSiteApi;
          const lastQueryArgs = { ...originalArgs, offset: _arg.offset - 25 };
          const lastQueryData = Object.values(queries).find((value) => lodash.isEqual(value?.originalArgs, lastQueryArgs));

          if (lastQueryData) {
            _api.updateCachedData((draft) => {
              draft.data.unshift(...(lastQueryData.data as any).data);
            });
          }
        } catch (error) {
          console.log(error);
        }
      }
    }),
    getVersion: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/version',
        responseHandler: (response): Promise<string> => response.text()
      })
    }),
    getApiName: builder.query<string, void>({
      query: () => ({
        url: '/diagnostics/apiName',
        responseHandler: (response): Promise<string> => response.text()
      })
    })
  })
});

export const { useGetSitesQuery, useGetWarehousesQuery, useLazyGetSitesQuery, useLazyGetWarehousesQuery, useGetVersionQuery, useGetApiNameQuery } = warehouseSiteApi;
